import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PatientSidebar from "../../components/PatientSidebar";
import ActivityDetailModal from "../../components/activityDetailModal";
import ActivityTaxonomy from "../../components/activityTaxonomy";
import activityTypeIcons from "../../assets/activity_types/icons";
import { API_URL } from "../../config/constants";
import { emitActivityViewed } from "../../activityEvents";
import TopBar from "../../components/TopBar";
import {
  Layout, Main,
  SectionTitle,
  TabsRow, Tab,
  ActivitiesGrid, ActivityCard, CardHeader, CardIcon, CardName, NewBadge,
  GoalLabel, GoalText, CardButton,
} from "./styles";

const PatientActivityPlan = () => {
  const location    = useLocation();
  const { t, i18n } = useTranslation();
  const token       = localStorage.getItem("token");

  const [activeTypeId, setActiveTypeId] = useState(location.state?.typeId || 1);
  const [activityTypes, setActivityTypes] = useState([]);
  const [prescribed, setPrescribed]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [openActivity, setOpenActivity] = useState(null);

  // Abrir la tarjeta la marca como vista para el terapeuta. No bloquea el
  // modal: si la petición falla, el paciente puede ver la actividad igual.
  const handleOpenActivity = (act) => {
    setOpenActivity(act);
    if (!act.viewed_this_week) {
      setPrescribed((prev) => prev.map((a) => (a.id === act.id ? { ...a, viewed_this_week: 1 } : a)));
    }
    fetch(`${API_URL}/me/activity-plan/${act.id}/view`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    })
      // Tras guardarse, para que la insignia de la barra lateral ya lo cuente.
      .then(() => { if (!act.viewed_this_week) emitActivityViewed(); })
      .catch((err) => console.error("mark viewed error:", err));
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [typesRes, planRes] = await Promise.all([
          fetch(`${API_URL}/activities/types`, { headers }),
          fetch(`${API_URL}/me/activity-plan?lang=${i18n.language}`, { headers }),
        ]);
        const typesData = await typesRes.json();
        const planData  = await planRes.json();
        setActivityTypes(Array.isArray(typesData) ? typesData : []);
        setPrescribed(Array.isArray(planData) ? planData : []);
      } catch (err) {
        console.error("PatientActivityPlan fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [i18n.language]); // eslint-disable-line

  const activeType       = activityTypes.find((t) => t.id === activeTypeId) || {};
  const typeActivities   = prescribed.filter((a) => a.activity_type_id === activeTypeId);

  return (
    <Layout>
      <TopBar />

      <PatientSidebar activeNav="activity-plan" />

      <Main>
        <SectionTitle>
          {t("activityPlan.typeTitle", {
            type: t(`activityTypes.${activeTypeId}.name`, { defaultValue: activeType.name || "" }),
          })}
        </SectionTitle>

        <TabsRow>
          {activityTypes.map((type) => (
            <Tab
              key={type.id}
              $active={type.id === activeTypeId}
              onClick={() => setActiveTypeId(type.id)}
            >
              {t(`activityTypes.${type.id}.name`, { defaultValue: type.name })}
            </Tab>
          ))}
        </TabsRow>

        {loading ? (
          <p style={{ color: "#999", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
            {t("activityPlan.loading")}
          </p>
        ) : typeActivities.length === 0 ? (
          <p style={{ color: "#999", fontSize: "0.9rem", textAlign: "center", padding: "40px 0" }}>
            {t("activityPlan.emptyCategory")}
          </p>
        ) : (
          <ActivitiesGrid>
            {typeActivities.map((act) => {
              const isNew = !act.viewed_this_week;
              return (
                <ActivityCard key={act.id} $isNew={isNew}>
                  <CardHeader>
                    <CardIcon src={activityTypeIcons[act.activity_type_id]} alt="" />
                    <CardName>{act.name}</CardName>
                    {isNew && <NewBadge>{t("activityPlan.new")}</NewBadge>}
                  </CardHeader>
                  <ActivityTaxonomy activity={act} />
                  {act.specific_goal && (
                    <>
                      <GoalLabel>{t("activityPlan.specificGoal")}</GoalLabel>
                      <GoalText>{act.specific_goal}</GoalText>
                    </>
                  )}
                  <CardButton onClick={() => handleOpenActivity(act)}>
                    {t("activityPlan.activityCard")}
                  </CardButton>
                </ActivityCard>
              );
            })}
          </ActivitiesGrid>
        )}
      </Main>

      {openActivity && (
        <ActivityDetailModal activity={openActivity} onClose={() => setOpenActivity(null)} />
      )}
    </Layout>
  );
};

export default PatientActivityPlan;
